import React, { useCallback, useEffect, useState } from 'react';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { useTranslation } from 'react-i18next';
import {
  getAvailability,
  setAvailability,
  getMailOrbitErrorKey,
  isMailOrbitMock,
} from 'src/utils/MailOrbitService';

// Task kapsami: Available / Away / On leave. Offline sunucudan gelirse yalnizca gosterilir.
const SELECTABLE = ['Available', 'Away', 'OnLeave'];

const AvailabilityControl = ({ onChanged }) => {
  const { t } = useTranslation(['mailorbitWork', 'mailorbit']);
  const [info, setInfo] = useState(null); // GET /me/availability yaniti
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [confirm, setConfirm] = useState(false);
  const [result, setResult] = useState(null); // OnLeave sonrasi yeniden dagitim ozeti

  const load = useCallback(async () => {
    try {
      const data = await getAvailability();
      setInfo(data);
      return data;
    } catch (e) {
      setError(getMailOrbitErrorKey(e));
      return null;
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      await load();
      if (!cancelled) setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [load]);

  const apply = async (status, confirmRedistribution) => {
    setSaving(true);
    setError(null);
    try {
      const res = await setAvailability(status, confirmRedistribution);
      // Mock yaniti sabittir; gercek modda sunucunun dondurdugu deger esas alinir.
      const next = isMailOrbitMock() ? status : (res && res.availability) || status;
      setInfo(prev => ({ ...(prev || {}), availability: next }));
      setResult(status === 'OnLeave' && res ? res.redistribution : null);
      if (onChanged) onChanged();
    } catch (e) {
      setError(getMailOrbitErrorKey(e));
      // Sunucu durumu esas: hata sonrasi yeniden oku.
      await load();
    } finally {
      setSaving(false);
      setConfirm(false);
    }
  };

  const handleSelect = async event => {
    const status = event.target.value;
    if (status === info.availability) return;
    if (status === 'OnLeave') {
      // Guncel acik is sayisi / strateji icin yeniden oku, sonra onay iste.
      setSaving(true);
      await load();
      setSaving(false);
      setConfirm(true);
      return;
    }
    apply(status, false);
  };

  if (loading) return <CircularProgress size={20} />;
  if (!info) {
    return error ? <Alert severity="error">{t(`mailorbit:${error}`)}</Alert> : null;
  }
  if (!info.hasAgentProfile) {
    return <Alert severity="info">{t('availability_no_profile')}</Alert>;
  }

  const options = SELECTABLE.includes(info.availability)
    ? SELECTABLE
    : [info.availability, ...SELECTABLE];

  return (
    <div>
      <FormControl variant="outlined" size="small" style={{ minWidth: 180 }} disabled={saving}>
        <InputLabel id="mo-availability-label">{t('availability_label')}</InputLabel>
        <Select
          labelId="mo-availability-label"
          label={t('availability_label')}
          value={info.availability || ''}
          onChange={handleSelect}
        >
          {options.map(o => (
            <MenuItem key={o} value={o} disabled={!SELECTABLE.includes(o)}>
              {t(`availability_${o}`, o)}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      {error ? (
        <Alert severity="error" style={{ marginTop: 8 }} onClose={() => setError(null)}>
          {t(`mailorbit:${error}`)}
        </Alert>
      ) : null}
      {result ? (
        <Alert severity="success" style={{ marginTop: 8 }} onClose={() => setResult(null)}>
          {t('leave_result', {
            reassigned: result.reassigned,
            returnedToPool: result.returnedToPool,
            skipped: result.skipped,
          })}
        </Alert>
      ) : null}

      <Dialog open={confirm} onClose={() => !saving && setConfirm(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('leave_confirm_title')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('leave_confirm_text', { count: info.openItemCount || 0 })}
          </DialogContentText>
          {info.leaveRedistributionStrategy ? (
            <Typography variant="body2">
              {t('leave_strategy')}: {String(info.leaveRedistributionStrategy)}
            </Typography>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(false)} disabled={saving}>
            {t('btn_cancel')}
          </Button>
          <Button onClick={() => apply('OnLeave', true)} color="primary" disabled={saving}>
            {t('leave_confirm_btn')}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default AvailabilityControl;
